// Forms (PRD §4.13). Build lead-capture forms, toggle them live, copy the public
// share link, and see submission counts. Submissions write back to subscribers.
import { useState } from 'react';
import { useI18n } from '../lib/i18n';
import { cn } from '../lib/cn';
import Modal from '../components/ui/Modal';
import Toggle from '../components/ui/Toggle';
import { useTenant } from '../lib/tenant';
import {
  useForms, saveForm, deleteForm, formFillUrl,
  type WaForm, type FormInput, type FormField, type FieldType,
} from '../lib/forms';

const inputCls = 'w-full rounded-[9px] border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary';
const FIELD_TYPES: FieldType[] = ['text', 'phone', 'email', 'number'];

function newField(): FormField {
  const id = (globalThis.crypto?.randomUUID?.() ?? `f_${Date.now()}_${Math.random().toString(36).slice(2)}`);
  return { id, label: '', type: 'text', required: false };
}
const emptyForm = (): FormInput => ({ name: '', enabled: true, fields: [{ ...newField(), label: 'Name' }, { ...newField(), label: 'Phone', type: 'phone', required: true }] });

export default function Forms() {
  const { t } = useI18n();
  const { tenantId } = useTenant();
  const { forms, loading } = useForms();
  const [editing, setEditing] = useState<FormInput | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState<WaForm | null>(null);

  function openEdit(f: WaForm) {
    setErr(null);
    setEditing({ id: f.id, name: f.name, enabled: f.enabled, fields: f.fields.length ? f.fields : [newField()] });
  }

  async function save() {
    if (!editing || !editing.name.trim()) return;
    const fields = editing.fields.filter((f) => f.label.trim());
    if (!fields.length) { setErr(t('forms.needField')); return; }
    setBusy(true); setErr(null);
    try { await saveForm({ ...editing, fields }); setEditing(null); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  async function doDelete() {
    if (!confirmDel) return;
    setBusy(true); setErr(null);
    try { await deleteForm(confirmDel.id); setConfirmDel(null); }
    // Previously no catch: a failed delete was an unhandled rejection, the
    // dialog stayed open and the user saw nothing.
    catch (e) { setErr((e as Error).message || t('forms.deleteFailed')); }
    finally { setBusy(false); }
  }

  async function copyLink(f: WaForm) {
    if (!tenantId) return;
    try { await navigator.clipboard.writeText(formFillUrl(tenantId, f.id)); setCopied(f.id); window.setTimeout(() => setCopied(null), 1500); } catch { /* noop */ }
  }

  function patchField(idx: number, patch: Partial<FormField>) {
    if (!editing) return;
    setEditing({ ...editing, fields: editing.fields.map((f, i) => (i === idx ? { ...f, ...patch } : f)) });
  }

  return (
    <div>
      <div className="mb-1 flex flex-wrap items-center gap-3">
        <h1 className="text-[22px] font-extrabold text-ink">{t('forms.title')}</h1>
        <button type="button" onClick={() => { setErr(null); setEditing(emptyForm()); }} className="rounded-[9px] bg-primary px-4 py-2 text-[13px] font-bold text-white sm:ms-auto">+ {t('forms.add')}</button>
      </div>
      <p className="mb-5 text-ink-3">{t('forms.subtitle')}</p>

      {loading ? (
        <div className="py-10 text-center text-sm text-ink-3">…</div>
      ) : forms.length === 0 ? (
        <div className="rounded-card border border-dashed border-slate-300 bg-white p-10 text-center">
          <div className="text-3xl">📝</div>
          <p className="mt-2 text-sm text-ink-3">{t('forms.empty')}</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {forms.map((f) => (
            <div key={f.id} className="flex flex-col rounded-card border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <b className="text-sm text-ink">{f.name}</b>
                <span className={cn('whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold', f.enabled ? 'bg-primary-50 text-primary' : 'bg-slate-100 text-ink-3')}>{f.enabled ? t('forms.live') : t('forms.off')}</span>
              </div>
              <p className="mt-1 text-xs text-ink-3">{f.fields.length} {t('forms.fields')} · {f.submissions} {t('forms.subs')}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" onClick={() => openEdit(f)} className="rounded-[8px] border border-slate-200 px-3 py-1.5 text-xs font-bold text-ink">{t('cat.edit')}</button>
                <button type="button" onClick={() => void copyLink(f)} className="rounded-[8px] border border-slate-200 px-3 py-1.5 text-xs font-bold text-primary">{copied === f.id ? t('set.api.copied') : t('forms.copyLink')}</button>
                <button type="button" onClick={() => setConfirmDel(f)} className="rounded-[8px] border border-red-500 px-3 py-1.5 text-xs font-bold text-red-500">{t('cat.delete')}</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Builder modal */}
      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing?.id ? t('forms.editTitle') : t('forms.add')}
        footer={
          <>
            <button type="button" onClick={() => setEditing(null)} className="rounded-[9px] border border-slate-200 bg-white px-4 py-2 text-[13px] font-bold text-ink">{t('common.cancel')}</button>
            <button type="button" onClick={save} disabled={busy || !editing?.name.trim()} className="rounded-[9px] bg-primary px-4 py-2 text-[13px] font-bold text-white disabled:opacity-40">{busy ? '…' : t('common.apply')}</button>
          </>
        }
      >
        {editing && (
          <div className="space-y-3">
            <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder={t('forms.name')} className={inputCls} autoFocus />
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-ink">{t('forms.enabled')}</span>
              <Toggle on={editing.enabled} onChange={(v) => setEditing({ ...editing, enabled: v })} label={t('forms.enabled')} />
            </div>
            <div className="space-y-2 border-t border-slate-200 pt-3">
              <span className="text-xs font-bold text-ink-2">{t('forms.fields')}</span>
              {editing.fields.map((fld, i) => (
                <div key={fld.id} className="flex flex-wrap items-center gap-2">
                  <input value={fld.label} onChange={(e) => patchField(i, { label: e.target.value })} placeholder={t('forms.fieldLabel')} className={cn(inputCls, 'flex-1 min-w-[120px]')} />
                  <select value={fld.type} onChange={(e) => patchField(i, { type: e.target.value as FieldType })} className={cn(inputCls, 'w-auto bg-white')}>
                    {FIELD_TYPES.map((ty) => <option key={ty} value={ty}>{t(`forms.type.${ty}`)}</option>)}
                  </select>
                  <label className="flex items-center gap-1 text-xs text-ink-2">
                    <input type="checkbox" checked={fld.required} onChange={(e) => patchField(i, { required: e.target.checked })} className="accent-primary" />
                    {t('forms.required')}
                  </label>
                  <button type="button" onClick={() => setEditing({ ...editing, fields: editing.fields.filter((_, x) => x !== i) })} className="rounded-[8px] border border-red-500 px-2 py-1 text-xs font-bold text-red-500">✕</button>
                </div>
              ))}
              <button type="button" onClick={() => setEditing({ ...editing, fields: [...editing.fields, newField()] })} className="rounded-[9px] border border-slate-200 px-3 py-1.5 text-xs font-bold text-ink">+ {t('forms.addField')}</button>
            </div>
            {err && <div className="rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{err}</div>}
          </div>
        )}
      </Modal>

      {/* Delete confirm */}
      <Modal
        open={confirmDel !== null}
        onClose={() => setConfirmDel(null)}
        title={t('cat.delete')}
        footer={
          <>
            <button type="button" onClick={() => setConfirmDel(null)} className="rounded-[9px] border border-slate-200 bg-white px-4 py-2 text-[13px] font-bold text-ink">{t('common.cancel')}</button>
            <button type="button" onClick={doDelete} disabled={busy} className="rounded-[9px] bg-red-500 px-4 py-2 text-[13px] font-bold text-white disabled:opacity-40">{t('cat.delete')}</button>
          </>
        }
      >
        <p className="text-sm text-ink-2">{confirmDel?.name}</p>
      </Modal>
    </div>
  );
}
